import PolicyLayout, { Section, P, Ul } from '../components/PolicyLayout';

const Step = ({ n, title, children }) => <div className="nc-deletion-step"><span>{n}</span><div><h3>{title}</h3><p>{children}</p></div></div>;

export default function DeleteAccount() {
  const mailSubject = encodeURIComponent('Account Deletion Request | NephroCore Nexus');
  const mailBody = encodeURIComponent(
    'Hello NephroCore team,\n\n' +
    'I would like to permanently delete my NephroCore account and associated data.\n\n' +
    'Registered name:\n' +
    'Registered email:\n' +
    'Registered mobile number:\n\n' +
    'I understand this action is permanent and cannot be undone.\n\nThank you.'
  );

  return (
    <PolicyLayout art="delete" title="Your account. Your choice." description="How to permanently delete your NephroCore account and the personal data associated with it.">
        <Section title="Overview">
          <P>
            NephroCore ("we," "our," or "us") gives you full control over your account. You may request deletion of your NephroCore account and personal data at any time, at no cost. This page explains how to make that request, what data is removed, and what we are legally required to keep.
          </P>
          <P>
            This applies to the <strong>NephroCore</strong> mobile application and any account created through it.
          </P>
        </Section>

        <Section title="Option 1 — Delete from inside the app">
          <Step n="1" title="Open the NephroCore app">Sign in with the account you want to delete.</Step>
          <Step n="2" title="Go to Profile">Tap the <strong>Profile</strong> tab at the bottom of the screen.</Step>
          <Step n="3" title="Open Settings">Tap <strong>Settings</strong> (gear icon) in the top right of the Profile screen.</Step>
          <Step n="4" title="Tap “Delete Account”">Scroll to the bottom and tap <strong>Delete Account</strong>.</Step>
          <Step n="5" title="Confirm">Read the confirmation notice and confirm. Your account is deactivated immediately and permanently deleted within 30 days.</Step>
        </Section>

        <Section title="Option 2 — Request deletion by email">
          <P>
            If you can no longer access the app or your account, email us from the address registered with your account and we will verify your identity and process the deletion.
          </P>
          <div className="nc-policy-note">
            <p style={{ fontWeight: 700, color: 'var(--dark)', marginBottom: 4 }}>Send your request to</p>
            <a href={`mailto:support@nephrocorenexus.com?subject=${mailSubject}&body=${mailBody}`} style={{ color: 'var(--primary)', fontWeight: 600 }}>support@nephrocorenexus.com</a>
            <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginTop: 8 }}>
              Subject: <strong>Account Deletion Request — NephroCore</strong>
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginTop: 4 }}>Mon–Sat, 10:00 AM – 6:00 PM IST</p>
          </div>
          <P>Please include the following so we can verify your identity:</P>
          <Ul items={[
            'Your registered full name',
            'Your registered email address',
            'Your registered mobile number',
            'A clear statement that you want your account and data permanently deleted',
          ]} />
          <P>
            We may ask one additional verification question before deleting your data. This protects your health records from deletion requests made by someone else.
          </P>
        </Section>

        <Section title="What gets deleted">
          <P>Once your request is processed, we permanently delete:</P>
          <Ul items={[
            'Your account and login credentials',
            'Profile information (name, date of birth, gender, profile photo)',
            'Contact information (email address, phone number, saved addresses)',
            'Identity verification details, including Aadhaar details held for verification',
            'Health profile data (medical history, current conditions, symptoms you recorded)',
            'Uploaded documents and lab reports stored in your account',
            'Consultation history and chat messages with doctors',
            'Membership status, reward points and referral history',
            'Device identifiers and push notification tokens',
          ]} />
        </Section>

        <Section title="What we must retain, and for how long">
          <P>
            A limited set of records cannot be deleted on request because Indian law requires us to keep them. These records are retained in restricted-access storage, are not used to contact you, and are not used for marketing or analytics.
          </P>
          <Ul items={[
            'Medical and diagnostic records associated with completed lab tests or consultations — retained for a minimum of 7 years, as required by applicable Indian healthcare regulations.',
            'Transaction and invoice records — retained as required by applicable tax and accounting law.',
            'Records needed to resolve an open dispute, chargeback, or legal claim — retained until the matter is closed.',
            'Anonymised, aggregated usage statistics that can no longer be linked back to you.',
          ]} />
          <P>
            Once the applicable retention period ends, these records are deleted or irreversibly anonymised.
          </P>
        </Section>

        <Section title="How long it takes">
          <Ul items={[
            'Immediately — your account is deactivated and you are signed out of all devices.',
            'Within 7 days — we acknowledge and verify email requests.',
            'Within 30 days — your personal data is permanently deleted from our active systems.',
            'Within 90 days — data is purged from encrypted backups as those backups expire on their normal rotation.',
          ]} />
        </Section>

        <Section title="Before you delete">
          <P>Account deletion is permanent and cannot be undone. Please note:</P>
          <Ul items={[
            'You will lose access to your stored lab reports and consultation history — download anything you need first.',
            'Any active membership plan is cancelled and unused benefits are forfeited, subject to our Refund Policy.',
            'Unredeemed reward points and referral credits are forfeited.',
            'Upcoming bookings and scheduled consultations are cancelled.',
            'Creating a new account later will not restore any of the deleted data.',
          ]} />
        </Section>

        <Section title="Delete specific data instead">
          <P>
            If you do not want to delete your entire account, you can instead ask us to remove specific data — for example, a particular uploaded report, or your saved address. Email the same address above describing exactly what you want removed, and we will action it without closing your account.
          </P>
        </Section>

        <Section title="Questions">
          <P>For anything related to your data or this deletion process, contact us at:</P>
          <div className="nc-policy-note">
            <p style={{ fontWeight: 700, color: 'var(--dark)', marginBottom: 4 }}>NephroCore</p>
            <a href="mailto:support@nephrocorenexus.com" style={{ color: 'var(--primary)', fontWeight: 600 }}>support@nephrocorenexus.com</a>
            <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginTop: 4 }}>Mon–Sat, 10:00 AM – 6:00 PM IST</p>
          </div>
        </Section>
    </PolicyLayout>
  );
}
